import {useState, useEffect, useRef} from 'react'
import {storage} from '@/utils/storage'
import {minusIcon, plusWhiteIcon} from '@img/index.js'

/**
 * NumberCounter — unified counter used across the portal.
 *
 * Modes:
 *  - Uncontrolled (booking): pass `id`. Component owns state, persists to
 *    sessionStorage at `hopn-counter-${id}`, broadcasts via
 *    `hopn-counter-update` window event so sibling instances stay in sync.
 *      <NumberCounter id="passengerType-123" max={4} onChange={(n) => ...}/>
 *
 *  - Controlled (edit): pass `value` + `onIncrement`/`onDecrement`.
 *      <NumberCounter value={n} onIncrement={...} onDecrement={...} buttonColor="white"/>
 *
 * Visual variants:
 *  - variant="edit" (default): Tailwind-styled rounded buttons + image icons.
 *  - variant="booking":         SCSS-styled `.hopn-btn`, `.hopn-count-value`.
 *
 * Edit-variant `buttonColor="white"` swaps the minus button background.
 */
export default function NumberCounter({
    id,
    initial = 0,
    min = 0,
    max = Infinity,
    onChange,
    value,
    onIncrement,
    onDecrement,
    buttonColor,
    variant = 'edit',
}) {
    const isControlled = value !== undefined
    const storageKey = id ? `hopn-counter-${id}` : null

    // The restored value is clamped to `min`/`max`: a count written before this counter
    // had a floor (or before the floor was raised) would otherwise come back below it
    // and sit there, since min is only enforced by the minus button.
    const [internal, setInternal] = useState(() => {
        if (!storageKey) return initial
        const saved = storage.get(storageKey, null)
        const restored = saved !== null ? parseInt(saved, 10) : initial
        return Number.isNaN(restored) ? min : Math.min(max, Math.max(min, restored))
    })

    const onChangeRef = useRef(onChange)
    const prevInitialRef = useRef(initial)

    useEffect(() => {
        onChangeRef.current = onChange
    })

    useEffect(() => {
        if (isControlled || !storageKey) return
        if (prevInitialRef.current === initial) return
        prevInitialRef.current = initial
        setInternal(initial)
    }, [initial, isControlled, storageKey])

    useEffect(() => {
        if (isControlled || !storageKey) return
        storage.set(storageKey, internal)
        onChangeRef.current?.(internal)
        window.dispatchEvent(
            new CustomEvent('hopn-counter-update', {detail: {key: storageKey, value: internal}})
        )
    }, [internal, storageKey, isControlled])

    useEffect(() => {
        if (isControlled || !storageKey) return
        const handler = (e) => {
            if (e.detail.key === storageKey && e.detail.value !== internal) {
                setInternal(e.detail.value)
            }
        }
        window.addEventListener('hopn-counter-update', handler)
        return () => window.removeEventListener('hopn-counter-update', handler)
    }, [storageKey, internal, isControlled])

    const count = isControlled ? value : internal

    const handleIncrease = () => {
        if (onIncrement) return onIncrement()
        if (isControlled) return
        setInternal(prev => Math.min(max, prev + 1))
    }
    const handleDecrease = () => {
        if (onDecrement) return onDecrement()
        if (isControlled) return
        setInternal(prev => Math.max(min, prev - 1))
    }

    if (variant === 'booking') {
        return (
            <>
                <button type="button" className="hopn-btn hopn-minus" onClick={handleDecrease}>
                    <img src={minusIcon} alt="Minus"/>
                </button>
                <span className="hopn-count-value">{count}</span>
                <button type="button" className="hopn-btn hopn-plus" onClick={handleIncrease}>
                    <img src={plusWhiteIcon} alt="Plus"/>
                </button>
            </>
        )
    }

    const minusBg = buttonColor === 'white' ? 'bg-white' : 'bg-(--bg-grey)'

    return (
        <>
            <button
                type="button"
                onClick={handleDecrease}
                className={`w-10 h-10 rounded-full ${minusBg} text-[22px] font-medium cursor-pointer flex justify-center items-center transition-colors border-0 text-(--text-primary) outline-0 shrink-0 p-0`}
            >
                <img src={minusIcon} alt="Minus"/>
            </button>
            <span className="text-[32px] w-10 text-center mx-2">{count}</span>
            <button
                type="button"
                onClick={handleIncrease}
                className="w-10 h-10 rounded-full bg-(--text-primary) text-white text-[22px] font-medium cursor-pointer flex justify-center items-center transition-colors border-0 outline-0 shrink-0 p-0"
            >
                <img src={plusWhiteIcon} alt="Plus"/>
            </button>
        </>
    )
}
